import { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import type { ReactNode } from "react";

// ---------- Toast ----------
type Toast = { kind: "ok" | "error" | "info"; msg: string } | null;
const ToastCtx = createContext<(t: Exclude<Toast, null>) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast>(null);
  const timer = useRef<number | undefined>(undefined);
  const notify = useCallback((t: Exclude<Toast, null>) => {
    setToast(t);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setToast(null), 4000);
  }, []);
  return (
    <ToastCtx.Provider value={notify}>
      {children}
      {toast && <div className={`toast ${toast.kind}`}>{toast.msg}</div>}
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);

/** 把 API 异常转成人类可读文案 */
export function errText(e: unknown): string {
  if (e && typeof e === "object" && "detail" in e) {
    const d = (e as { detail: unknown }).detail;
    if (typeof d === "string") return d;
    if (d && typeof d === "object") {
      const obj = d as Record<string, unknown>;
      if (typeof obj.message === "string") return obj.message;
      return JSON.stringify(d);
    }
  }
  return e instanceof Error ? e.message : String(e);
}

// ---------- JSON 编辑框 ----------
export function JsonArea({
  value, onChange, rows = 5,
}: {
  value: Record<string, unknown>;
  onChange: (v: Record<string, unknown>) => void;
  rows?: number;
}) {
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [err, setErr] = useState<string | null>(null);

  // 外部 value 变化时同步（如加载完成）
  const lastExternal = useRef(text);
  useEffect(() => {
    const s = JSON.stringify(value, null, 2);
    if (s !== lastExternal.current) {
      lastExternal.current = s;
      setText(s);
      setErr(null);
    }
  }, [value]);

  return (
    <div>
      <textarea
        rows={rows}
        value={text}
        spellCheck={false}
        onChange={(e) => {
          const t = e.target.value;
          setText(t);
          try {
            const parsed = t.trim() === "" ? {} : JSON.parse(t);
            setErr(null);
            onChange(parsed);
          } catch {
            setErr("JSON 格式错误");
          }
        }}
      />
      {err && <div className="err" style={{ fontSize: 12 }}>{err}</div>}
    </div>
  );
}

// ---------- 小组件 ----------
export function DirtyChip({ dirty }: { dirty: boolean }) {
  return dirty ? <span className="badge dirty">草稿</span> : null;
}
export function EnabledChip({ enabled }: { enabled: boolean }) {
  return <span className={`badge ${enabled ? "on" : "off"}`}>{enabled ? "● 启用" : "○ 停用"}</span>;
}
